import type { CSSProperties, ReactNode } from "react";
import React from "react";

import {
  E_PillSurface,
  resolvePillSurface,
  shouldDimDisabledPill,
  type T_PillTone,
} from "@lattice/shared-frontend/components";

import { InteractiveSurface, type T_KitCssVars } from "./InteractiveSurface";
import "./kit.css";

export type { T_PillTone };

export type T_PillProps = {
  ariaLabel?: string;
  disabled?: boolean;
  label: string;
  leading?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  style?: CSSProperties;
  tone?: T_PillTone;
  trailing?: ReactNode;
};

/**
 * Canonical compact capsule for actions, selections, and readouts. Without an
 * onClick it renders presentation-only content that can safely sit inside an
 * external trigger such as DropdownMenu without nesting buttons.
 */
export function Pill({
  ariaLabel,
  disabled = false,
  label,
  leading,
  onClick,
  selected,
  style,
  tone = "neutral",
  trailing,
}: T_PillProps) {
  // Surface ROLE is decided by the shared rule; this file only maps the role
  // to web's token representation (RAI-1091).
  const surface = resolvePillSurface({ selected, tone });
  const backgroundColor =
    surface === E_PillSurface.ACCENT
      ? "var(--lattice-chatkit-purple-tint-bg)"
      : "var(--lattice-chatkit-chip-bg)";
  const pillStyle: CSSProperties & T_KitCssVars = {
    "--lk-bg": backgroundColor,
    ...style,
  };
  const className = [
    "lk-pill",
    shouldDimDisabledPill({ disabled, selected }) ? "lk-pill--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const content = (
    <>
      {leading}
      <span className="lk-pill__label">{label}</span>
      {trailing}
    </>
  );

  if (onClick === undefined) {
    return (
      <span className={className} style={pillStyle}>
        {content}
      </span>
    );
  }

  return (
    <InteractiveSurface
      ariaLabel={ariaLabel ?? label}
      ariaPressed={selected}
      backgroundColor={backgroundColor}
      className={className}
      disabled={disabled}
      onClick={onClick}
      pressedBackgroundColor="var(--lattice-dark-grey-dark-4)"
      style={pillStyle}
    >
      {content}
    </InteractiveSurface>
  );
}
